import { MAX_DIMENSION } from '../limits';

/**
 * Export formats and file-name rules. Pure: the browser-only parts (encoding
 * probes, decoding, downloads) live in src/io.
 */

export type ExportFormat = 'png' | 'jpeg' | 'webp';

export interface ExportFormatSpec {
  id: ExportFormat;
  label: string;
  mime: string;
  extension: string;
  /** Whether the format has a meaningful quality knob. */
  lossy: boolean;
}

export const EXPORT_FORMATS: readonly ExportFormatSpec[] = [
  { id: 'png', label: 'PNG', mime: 'image/png', extension: 'png', lossy: false },
  { id: 'jpeg', label: 'JPEG', mime: 'image/jpeg', extension: 'jpg', lossy: true },
  { id: 'webp', label: 'WebP', mime: 'image/webp', extension: 'webp', lossy: true },
] as const;

/** Formats we accept on open. SVG is decoded as a rasterised image. */
export const ACCEPTED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/avif',
  'image/svg+xml',
] as const;

export const FILE_INPUT_ACCEPT = ACCEPTED_IMAGE_TYPES.join(',');

export function formatSpec(format: ExportFormat): ExportFormatSpec {
  const spec = EXPORT_FORMATS.find((candidate) => candidate.id === format);
  if (!spec) throw new Error(`Unknown export format: ${format}`);
  return spec;
}

export function isAcceptedImageFile(file: { type?: string; name?: string }): boolean {
  if (file.type && (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(file.type)) return true;
  // Some platforms report an empty MIME type; fall back to the extension.
  const extension = file.name?.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  return (
    extension !== undefined &&
    ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'avif', 'svg'].includes(extension)
  );
}

/** Strip path separators and characters that are unsafe in file names. */
export function sanitizeFileName(name: string): string {
  return name
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\.{2,}/g, '.')
    .replace(/^\.+/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
}

/** Replace (or add) the extension so it matches the chosen format. */
export function withExtension(name: string, format: ExportFormat): string {
  const base = sanitizeFileName(name).replace(/\.[a-z0-9]+$/i, '');
  return `${base || 'image'}.${formatSpec(format).extension}`;
}

export function scaleDimensions(width: number, height: number, scale: number) {
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Which export formats this browser can actually encode.
 *
 * `canvas.toDataURL` silently returns PNG when it cannot encode the requested
 * type, so the only reliable test is to ask and look at what came back.
 */
export function detectSupportedFormats(
  createCanvas: () => HTMLCanvasElement | OffscreenCanvas = () => document.createElement('canvas'),
): Record<ExportFormat, boolean> {
  const result = { png: true, jpeg: false, webp: false } as Record<ExportFormat, boolean>;
  try {
    const canvas = createCanvas();
    canvas.width = 1;
    canvas.height = 1;
    if (!canvas.getContext('2d')) return result;
    const toDataUrl = (mime: string) => {
      // OffscreenCanvas has no toDataURL, so only probe real canvas elements.
      if (!('toDataURL' in canvas)) return false;
      try {
        return canvas.toDataURL(mime, 0.8).startsWith(`data:${mime}`);
      } catch {
        return false;
      }
    };
    result.jpeg = toDataUrl('image/jpeg');
    result.webp = toDataUrl('image/webp');
  } catch {
    // No canvas at all: only claim PNG, which every target browser supports.
  }
  return result;
}

/** Clamp a requested export scale to something sane. */
export function clampExportScale(scale: number): number {
  if (!Number.isFinite(scale)) return 1;
  return Math.min(8, Math.max(0.05, scale));
}

/** Largest scale at which the exported image still fits the size limit. */
export function maxExportScale(width: number, height: number): number {
  return Math.min(1, MAX_DIMENSION / Math.max(width, height));
}
